import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import { TransactionActionsMenu } from '@/components/Payouts/TransactionActionsMenu';
import { TransactionEventCell } from '@/components/Payouts/TransactionEventCell';
import { HoverOnlyTooltipComponent } from '@/components/Tooltip';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { DATE_FORMAT } from '@/helpers/dates';
import { PayoutTransactionClientData } from '@/types/Payouts';
import { Badge, BadgeVariant, Box, Column, Icon, Table } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { Key, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  PayoutTransactionLevel,
  ProjectTransactionStatus,
  ProjectTransactionStatusMap,
  TransactionLevelFilterMap,
  UserPermissions,
} from 'types';

import { TransactionDetailsView } from './admin/TransactionDetailsView';

export interface PayoutTransactionTableProps {
  transactions: PayoutTransactionClientData[];
  selectedIds?: string[];
  handleRowSelected?(ids: string[], selected: boolean): void;
  hideColumns?: PayoutTransactionTableColumnName[];
  showExpandedRow?: boolean;
  isCompact?: boolean;
  showContextMenu?: boolean;
  disableSelection?: boolean;
  transactionLevel?: PayoutTransactionLevel;
}

const statusBadgeMap: Record<ProjectTransactionStatus, BadgeVariant> = {
  [ProjectTransactionStatus.open]: 'default',
  [ProjectTransactionStatus.paid]: 'success',
  [ProjectTransactionStatus.pending]: 'warning',
  [ProjectTransactionStatus.approved]: 'primary',
  [ProjectTransactionStatus.migrated]: 'info',
  [ProjectTransactionStatus.historical]: 'tertiary',
  [ProjectTransactionStatus.adjustment]: 'tertiary',
  [ProjectTransactionStatus.paused]: 'danger',
};

const transactionLevelBadgeMap: Record<PayoutTransactionLevel, BadgeVariant> = {
  [PayoutTransactionLevel.project]: 'default',
  [PayoutTransactionLevel.payee]: 'info',
};

export type PayoutTransactionTableColumnName =
  | 'id'
  | 'eventDate'
  | 'transactionLevel'
  | 'projectExternalId'
  | 'project.name'
  | 'payee.name'
  | 'tags'
  | 'event'
  | 'batch.batchDate'
  | 'status'
  | 'amount'
  | 'contextMenu'
  | 'pauseExpirationDate';

const getTransactionColumns = (
  hideColumns: PayoutTransactionTableColumnName[],
  handleRowSelected: PayoutTransactionTableProps['handleRowSelected'],
  selectedIds: string[],
  transactions: PayoutTransactionClientData[],
  showContextMenu = false,
  transactionLevel?: PayoutTransactionLevel,
) =>
  [
    handleRowSelected && {
      heading: (
        <input
          type="checkbox"
          name="selectedTransaction"
          checked={Boolean(transactions?.length && transactions.length === selectedIds?.length)}
          onChange={() =>
            handleRowSelected(
              transactions?.map((transaction) => `${transaction.id}`) || [],
              !(transactions?.length && transactions.length === selectedIds?.length),
            )
          }
        />
      ),
      dataKey: 'id',
      render: (_cell?: any, row?: any) =>
        row ? (
          <input
            type="checkbox"
            name="selectedTransaction"
            checked={selectedIds?.includes(row.id)}
            onChange={() => handleRowSelected([row.id], !selectedIds?.includes(row.id))}
          />
        ) : (
          <></>
        ),
    },
    {
      heading: 'Event Date & Time',
      dataKey: 'eventDate',
      render: (cell?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          <DateTimeDisplay value={cell} fontSize="xs" dateFormat="DD h:mm:ss a" inline />
        </Box>
      ),
    },
    {
      heading: 'Transaction Level',
      dataKey: 'transactionLevel',
      render: (cell?: any) =>
        cell ? (
          <Badge
            variant={transactionLevelBadgeMap[cell as PayoutTransactionLevel] ?? 'default'}
            message={TransactionLevelFilterMap[cell as PayoutTransactionLevel] ?? cell}
          />
        ) : null,
    },
    {
      heading: 'Finco Account Id',
      dataKey: 'projectExternalId',
      render: (cell?: any, _row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {cell}
        </Box>
      ),
    },
    {
      heading: 'Account',
      dataKey: 'project.name',
      // An organization-level row has no project behind it, so there is nothing to name and nowhere
      // for the link to go. Reading row.project.name unguarded is what used to take the whole table
      // down as soon as one appeared.
      render: (_cell?: any, row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {row?.project ? (
            <Link to={`/accounts/${row?.projectExternalId}/funding`} target="_blank">
              <Box childGap="2xs" direction="row">
                <Box>{row.project.name}</Box>
              </Box>
            </Link>
          ) : (
            '-'
          )}
        </Box>
      ),
    },
    {
      heading: transactionLevel === PayoutTransactionLevel.payee ? 'Organization' : 'Payee',
      dataKey: 'payee.name',
      render: (_cell?: any, row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {row.payee.name}
        </Box>
      ),
    },
    {
      heading: '',
      dataKey: 'tags',
      render: (_cell?: any, row?: any) => (
        <Box direction="row" fontWeight="medium" childGap="xs">
          {row?.isManual ? <Badge variant="secondary" message="M" size="sm" /> : null}
          {[ProjectTransactionStatus.open, ProjectTransactionStatus.pending].includes(row?.status) &&
          row?.payee?.payoutHold?.active ? (
            <Badge variant="danger" message="H" size="sm" />
          ) : null}
        </Box>
      ),
    },
    {
      heading: 'Payout Event',
      dataKey: 'event',
      render: (_cell?: any, row?: any) => <TransactionEventCell transaction={row} />,
    },
    {
      heading: 'Batch',
      dataKey: 'batch.batchDate',
      render: (_cell?: any, row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {row.batch ? (
            <Link to={`/admin/payouts/batches/${row?.batchId}`} target="_blank">
              {DateTime.fromISO(row.batch.batchDate).toFormat(DATE_FORMAT)}
            </Link>
          ) : (
            '-'
          )}
        </Box>
      ),
    },
    {
      heading: 'Status',
      dataKey: 'status',
      render: (cell?: any, row?: any) => (
        <Box fontWeight="medium" as="span" display="inline">
          {cell && row.pauseExpirationDate ? (
            <HoverOnlyTooltipComponent
              displayedContainerStyle={{
                display: 'inline',
              }}
              contentStyle={{
                fontWeight: 'regular',
                width: 'xs',
                borderWidth: 'xs',
                borderColor: 'danger',
              }}
              onClose={() => {}}
              content={'Expiration date: ' + row.pauseExpirationDate}
            >
              <Badge
                variant={statusBadgeMap[cell as ProjectTransactionStatus]}
                message={ProjectTransactionStatusMap[cell as ProjectTransactionStatus]}
              />
            </HoverOnlyTooltipComponent>
          ) : (
            <Badge
              variant={statusBadgeMap[cell as ProjectTransactionStatus]}
              message={ProjectTransactionStatusMap[cell as ProjectTransactionStatus]}
            />
          )}
        </Box>
      ),
    },
    {
      heading: <Box alignItems="flex-start">Amount</Box>,
      dataKey: 'amount',
      render: (cell?: any, row?: any) => {
        const isClawback = row?.eventType === 'clawback' || !!row.clawbackRuleName;
        const isInvoiceMayIncludeSalesTax = row?.attributes?.invoiceMayIncludeSalesTax;

        let textColor: any = '';
        if (isClawback) {
          textColor = 'danger-500';
        }
        if (isInvoiceMayIncludeSalesTax) {
          textColor = 'warning-500';
        }

        const iconElement = <Icon name="t-warning" size="xl" color={textColor} />;

        return (
          <Box alignItems="center" direction="row" justifyContent="flex-end">
            {isInvoiceMayIncludeSalesTax ? (
              <HoverOnlyTooltipComponent
                displayedContainerStyle={{
                  display: 'inline',
                }}
                contentStyle={{
                  fontWeight: 'regular',
                  width: 'xs',
                  borderWidth: 'xs',
                  borderColor: 'warning',
                }}
                onClose={() => {}}
                content={'Invoice may include sales tax'}
              >
                {iconElement}
              </HoverOnlyTooltipComponent>
            ) : isClawback ? (
              iconElement
            ) : null}
            <Box fontWeight="medium" color={textColor} alignContent="flex-end">
              {cell ? currencyFormatter.format(cell) : '-'}
            </Box>
          </Box>
        );
      },
    },
    {
      heading: 'Pause Expiration Date',
      dataKey: 'pauseExpirationDate',
      render: (cell?: any) => (
        <Box fontWeight="medium" alignItems="center" direction="row" justifyContent="flex-end">
          {cell}
        </Box>
      ),
    },
    showContextMenu
      ? {
          heading: '',
          dataKey: 'contextMenu',
          width: 20,
          render: (_cell?: any, row?: any) => {
            if (
              [
                ProjectTransactionStatus.pending,
                ProjectTransactionStatus.open,
                ProjectTransactionStatus.historical,
                ProjectTransactionStatus.adjustment,
                ProjectTransactionStatus.paused,
                ProjectTransactionStatus.migrated,
              ].includes(row.status)
            ) {
              return <TransactionActionsMenu transaction={row} />;
            }
          },
        }
      : {},
  ].filter((column) => column && !hideColumns.includes(column.dataKey as PayoutTransactionTableColumnName));

export const PayoutTransactionTable = ({
  transactions,
  selectedIds,
  handleRowSelected,
  hideColumns,
  isCompact,
  showExpandedRow = false,
  showContextMenu = false,
  disableSelection = false,
  transactionLevel,
}: PayoutTransactionTableProps) => {
  const [expandedRow, setExpandedRow] = useState<Key | undefined>(undefined);
  const { claims } = useAuth();
  const includeMenu = useMemo(
    () =>
      isPalmettoFinanceUser(claims) &&
      claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin) &&
      showContextMenu,
    [claims, showContextMenu],
  );

  const isLightReachAccountingAdminOrReader =
    isPalmettoFinanceUser(claims) &&
    (claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin) ||
      claims?.permissions?.includes(UserPermissions.lightReachAccountingReader));

  const columns = useMemo(
    () =>
      getTransactionColumns(
        hideColumns ?? [],
        disableSelection ? undefined : handleRowSelected,
        selectedIds ?? [],
        transactions,
        includeMenu,
        transactionLevel,
      ),
    [hideColumns, disableSelection, handleRowSelected, selectedIds, transactions, includeMenu, transactionLevel],
  );
  return transactions ? (
    <Table
      rowKey="transactionId"
      isScrollable={{ x: true }}
      columns={columns as Column[]}
      isCompact={isCompact}
      rows={transactions}
      {...(isLightReachAccountingAdminOrReader && showExpandedRow
        ? {
            expandedRow,
            onExpandedRowChange: (expandedRow) => setExpandedRow(expandedRow),
            expandedRowRender: (row: any) => <TransactionDetailsView transaction={row} />,
            expandBoxProps: {
              cursor: 'pointer',
              fontSize: 'xs',
            },
          }
        : {})}
    />
  ) : null;
};
